import { NumberedStep } from "@appcaire/ui";
import { approvedProductImageFile } from "@/components/marketing/product-shot/websiteProductImage";
import type { SyncSceneCopy } from "./brandHomeCopy";
import type { LandingLocale } from "../landingCopy";
import "./shared-plan-scene.css";

const PLANNER_IMAGE_ID = "planning.calendar.full-day";
const EMPLOYEE_IMAGE_ID = "mobile.employee.schedule";
const CLIENT_IMAGE_ID = "mobile.client.visits";

/**
 * The planner's calendar between the staff and client apps, in two acts:
 * the planner changes a visit and both apps get it; then the staff app's GPS
 * reports a late arrival that reaches the calendar and the client app.
 * Reduced motion shows the first act still; the steps below tell both.
 */
export function SharedPlanScene({
  locale,
  copy,
}: {
  locale: LandingLocale;
  copy: SyncSceneCopy;
}) {
  const planner = approvedProductImageFile(PLANNER_IMAGE_ID, locale);
  const employee = approvedProductImageFile(EMPLOYEE_IMAGE_ID, locale);
  const client = approvedProductImageFile(CLIENT_IMAGE_ID, locale);
  return (
    <figure className="sp" aria-label={copy.label} data-shared-plan-scene>
      <div className="sp__stage">
        <div className="sp__phone sp__phone--employee">
          <p className="sp__label">{copy.employee}</p>
          <div className="sp__screen">
            <img
              src={employee.src}
              alt={employee.alt}
              width={employee.width}
              height={employee.height}
              loading="lazy"
            />
            <p className="sp__toast sp__toast--a-employee">
              {copy.employeeGetsChange}
            </p>
            <p className="sp__toast sp__toast--b-employee sp__toast--alert">
              {copy.employeeLate}
            </p>
          </div>
        </div>
        <div className="sp__link sp__link--left" aria-hidden="true">
          <span className="sp__arrow sp__arrow--a-left">←</span>
          <span className="sp__arrow sp__arrow--b-left">→</span>
        </div>
        <div className="sp__desk">
          <p className="sp__label">{copy.planner}</p>
          <div className="sp__monitor">
            <img
              src={planner.src}
              alt={planner.alt}
              width={planner.width}
              height={planner.height}
              loading="lazy"
            />
            <p className="sp__chip sp__chip--a">{copy.plannerChange}</p>
            <p className="sp__chip sp__chip--b">{copy.plannerGetsEta}</p>
          </div>
        </div>
        <div className="sp__link sp__link--right" aria-hidden="true">
          <span className="sp__arrow sp__arrow--a-right">→</span>
          <span className="sp__arrow sp__arrow--b-right">→</span>
        </div>
        <div className="sp__phone sp__phone--client">
          <p className="sp__label">{copy.client}</p>
          <div className="sp__screen">
            <img
              src={client.src}
              alt={client.alt}
              width={client.width}
              height={client.height}
              loading="lazy"
            />
            <p className="sp__toast sp__toast--a-client">
              {copy.clientGetsChange}
            </p>
            <p className="sp__toast sp__toast--b-client">
              {copy.clientGetsEta}
            </p>
          </div>
        </div>
      </div>
      <figcaption>
        <ol className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {copy.steps.map((step, i) => (
            <NumberedStep
              key={step}
              n={i + 1}
              title={step}
              titleAs="p"
              domain="human"
            />
          ))}
        </ol>
      </figcaption>
    </figure>
  );
}
